<template>
<div v-if="visible" class="p-4">
  <h4 class="mb-3">Vorschau</h4>
  <p class="text-muted mb-4">
    Überprüfe alle Angaben bevor du die {{ wizard.documentLabel }} erstellst.
  </p>

  <div class="receipt-preview border rounded p-4 bg-light">
    <div class="text-center mb-4">
      <h3>{{ wizard.documentLabel }}</h3>
    </div>

    <div class="row mb-3">
      <div class="col-md-6" v-if="selectedTeam">
        <strong>Bereich:</strong> {{ selectedTeam?.name }}
      </div>
      <div class="col-md-6" v-else>
        <strong>Persönliche Quittung</strong>
      </div>
      <div class="col-md-6" v-if="formData.recipientEmail">
        <strong>Empfänger-E-Mail:</strong> {{ formData.recipientEmail }}
      </div>
    </div>

    <!-- Additional fields -->
    <div v-if="hasVisibleAdditionalFields" class="mb-3">
      <strong>Zusätzliche Felder:</strong>

      <!-- Personal Fields -->
      <div
        v-if="personalGroupedFields.length > 0 || personalAdditionalFields.length > 0"
        class="mt-3"
      >
        <h6 class="text-primary mb-2">
          <font-awesome-icon :icon="getCategoryIcon('personal')" class="me-2" />
          {{ getCategoryLabel('personal') }}
        </h6>
        <div
          v-for="group in personalGroupedFields"
          :key="`personal-group-${group.id}`"
          class="mb-3"
        >
          <div class="d-flex align-items-center mb-2">
            <span
              class="badge rounded-pill fw-semibold"
              :class="getContrastTextClass(group.color || '#E9ECEF')"
              :style="{ backgroundColor: group.color || '#E9ECEF' }"
            >
              {{ group.name }}
            </span>
          </div>
          <div class="row">
            <div
              v-for="item in group.fields"
              :key="`${group.id}-${item.key}`"
              class="col-md-6 mb-2"
            >
              <div class="small">
                <strong>{{ item.key }}:</strong>
                <span v-if="isFileField(item.key)">
                  {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                  ausgewählt
                </span>
                <span v-else>
                  {{ item.display }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-if="personalAdditionalFields.length > 0">
          <div
            v-for="item in personalAdditionalFields"
            :key="item.key"
            class="col-md-6 mb-2"
          >
            <div class="small">
              <strong>{{ item.key }}:</strong>
              <span v-if="isFileField(item.key)">
                {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                ausgewählt
              </span>
              <span v-else>
                {{ item.display }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Receipt Fields -->
      <div
        v-if="hasReceiptSection"
        class="mt-3"
      >
        <h6 class="text-success mb-2">
          <font-awesome-icon :icon="getCategoryIcon('receipt')" class="me-2" />
          {{ getCategoryLabel('receipt') }}
        </h6>
        <div
          v-for="group in receiptGroupedFields"
          :key="`receipt-group-${group.id}`"
          class="mb-3"
        >
          <div class="d-flex align-items-center mb-2">
            <span
              class="badge rounded-pill fw-semibold"
              :class="getContrastTextClass(group.color || '#E9ECEF')"
              :style="{ backgroundColor: group.color || '#E9ECEF' }"
            >
              {{ group.name }}
            </span>
          </div>
          <div class="row">
            <div
              v-for="item in group.fields"
              :key="`${group.id}-${item.key}`"
              class="col-md-6 mb-3"
            >
              <div class="small">
                <strong class="me-2">{{ item.key }}:</strong>
                <span v-if="isFileField(item.key)">
                  {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                  ausgewählt
                </span>
                <span v-else>
                  {{ item.display }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-if="receiptStandaloneFields.length > 0">
          <div
            v-for="item in receiptStandaloneFields"
            :key="item.key"
            class="col-md-6 mb-3"
          >
            <div class="small">
              <strong class="me-2">{{ item.key }}:</strong>
              <span v-if="isFileField(item.key)">
                {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                ausgewählt
              </span>
              <span v-else>
                {{ item.display }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Financial Fields -->
      <div
        v-if="hasFinancialSection"
        class="mt-3"
      >
        <h6 class="text-warning mb-2">
          <font-awesome-icon :icon="getCategoryIcon('financial')" class="me-2" />
          {{ getCategoryLabel('financial') }}
        </h6>
        <div
          v-for="group in financialGroupedFields"
          :key="`financial-group-${group.id}`"
          class="mb-3"
        >
          <div class="d-flex align-items-center mb-2">
            <span
              class="badge rounded-pill fw-semibold"
              :class="getContrastTextClass(group.color || '#E9ECEF')"
              :style="{ backgroundColor: group.color || '#E9ECEF' }"
            >
              {{ group.name }}
            </span>
          </div>
          <div class="row">
            <div
              v-for="item in group.fields"
              :key="`${group.id}-${item.key}`"
              class="col-md-6 mb-2"
            >
              <div class="small">
                <strong>{{ item.key }}:</strong>
                <span v-if="isFileField(item.key)">
                  {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                  ausgewählt
                </span>
                <span v-else>
                  {{ item.display }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-if="financialStandaloneFields.length > 0">
          <div
            v-for="item in financialStandaloneFields"
            :key="item.key"
            class="col-md-6 mb-2"
          >
            <div class="small">
              <strong>{{ item.key }}:</strong>
              <span v-if="isFileField(item.key)">
                {{ getFileCount(item.key) }} Datei{{ getFileCount(item.key) > 1 ? 'en' : '' }}
                ausgewählt
              </span>
              <span v-else>
                {{ item.display }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hochgeladene Dateien in der Vorschau -->
    <div v-if="Object.keys(uploadedFiles).length > 0" class="mb-3">
      <strong>Hochgeladene Dateien:</strong>
      <div class="mt-2">
        <div v-for="(files, fieldName) in uploadedFiles" :key="fieldName" class="mb-2">
          <div v-if="Array.isArray(files) && files.length > 0">
            <div class="small text-muted mb-1">{{ fieldName }}:</div>
            <div
              v-for="(file, index) in files"
              :key="index"
              class="d-flex align-items-center mb-1"
            >
              <font-awesome-icon :icon="getFileIcon(file.type)" class="me-2 small" />
              <span class="small">{{
                file.originalName || file.name || `Datei ${index + 1}`
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kombinierte Positionen -->
    <div v-if="hasCombinedRows" class="mb-4">
      <h6 class="text-primary mb-2">
        <font-awesome-icon icon="layer-group" class="me-2" />
        Positionen
      </h6>
      <ReceiptBillingTable
        :table-data="combinedTableDisplay"
        empty-label="Keine Positionen vorhanden"
      />
    </div>

    <div v-if="needsSignature && hasSignature" class="text-center mt-4">
      <h6 class="text-muted mb-3">UNTERSCHRIFT</h6>
      <div class="signature-preview">
        <img 
          :src="signatureDataURL" 
          alt="Unterschrift" 
          class="img-fluid border rounded bg-white"
          style="max-height: 150px;"
        >
      </div>
    </div>
    
    <div v-if="!needsSignature" class="text-center mt-4">
      <div class="alert alert-info">
        <font-awesome-icon icon="info-circle" class="me-2" />
        <strong>Hinweis:</strong> Für dieses Design ist keine Signatur erforderlich.
      </div>
    </div>
  </div>
</div>

</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import ReceiptBillingTable from '@/components/receipt/ReceiptBillingTable.vue'
import { buildReceiptTables, DEFAULT_COLUMNS } from '@/utils/billingTableBuilder'
import { groupFieldsByGroupId, getContrastTextClass } from '@/utils/receiptFieldUtils'
import { ReceiptWizardSymbol } from '../../tokens'

const wizard = inject(ReceiptWizardSymbol)

if (!wizard) {
  throw new Error('StepReview requires ReceiptWizard context')
}

const visible = computed(() => wizard.isCurrentStep('Vorschau'))
const selectedTeam = wizard.selectedTeam
const formData = wizard.formData
const formattedFieldsDataByCategory = wizard.formattedFieldsDataByCategory
const fieldGroupsByCategory = wizard.fieldGroupsByCategory
const getCategoryLabel = wizard.getCategoryLabel
const getCategoryIcon = wizard.getCategoryIcon
const isFileField = wizard.isFileField
const getFileCount = wizard.getFileCount
const getFormattedFieldValue = wizard.getFormattedFieldValue

const isTaxRateFieldName = (name) => typeof name === 'string' && name.endsWith('_taxRate')
const shouldHideField = (fieldInfo, fallbackKey) => {
  const fieldName = fieldInfo?.fieldName || fallbackKey
  return isTaxRateFieldName(fieldName)
}

const buildCategoryFieldLists = (category, filterFn) =>
  groupFieldsByGroupId(
    formattedFieldsDataByCategory.value?.[category] || {},
    fieldGroupsByCategory.value?.[category] || [],
    {
      getDisplay: (fieldInfo, key) => {
        const fieldName = fieldInfo.fieldName || key
        return getFormattedFieldValue(fieldInfo.value, fieldName, fieldInfo.value)
      },
      filter: (fieldInfo, key, display) => {
        if (!fieldInfo || shouldHideField(fieldInfo, key)) return false
        const fieldName = fieldInfo.fieldName || key
        if (filterFn && !filterFn(fieldInfo, key, fieldName, display)) return false
        const isFile = isFileField(key)
        if (display === '-' || display === '' || display === null || display === undefined) {
          return isFile
        }
        return true
      },
      transformField: ({ key, fieldInfo, display }) => ({ key, fieldInfo, display }),
    }
  )

const personalFieldLists = computed(() => buildCategoryFieldLists('personal'))
const personalGroupedFields = computed(() => personalFieldLists.value.groups)
const personalAdditionalFields = computed(() => personalFieldLists.value.ungrouped)

const createEmptyTable = () => ({
  columns: DEFAULT_COLUMNS,
  currencySymbol: '€',
  rows: [],
  summary: {
    net: 0,
    tax: 0,
    gross: 0,
    display: {
      net: '0.00€',
      tax: '0.00€',
      gross: '0.00€',
    },
  },
  taxBreakdown: [],
})

const combinedTable = ref(createEmptyTable())
const combinedTableDisplay = computed(() => combinedTable.value || createEmptyTable())
const fieldsUsedInTable = ref(new Set())
const hasCombinedRows = computed(() => combinedTableDisplay.value.rows.length > 0)
const formDataData = computed(() => formData.value?.data || {})

const isFieldInCombinedTable = (fieldName) => {
  if (!fieldName) return false
  return fieldsUsedInTable.value.has(fieldName)
}

const receiptFieldLists = computed(() =>
  buildCategoryFieldLists('receipt', (fieldInfo, key, fieldName) => !isFieldInCombinedTable(fieldName))
)
const receiptGroupedFields = computed(() => receiptFieldLists.value.groups)
const receiptStandaloneFields = computed(() => receiptFieldLists.value.ungrouped)

const financialFieldLists = computed(() =>
  buildCategoryFieldLists('financial', (fieldInfo, key, fieldName) => !isFieldInCombinedTable(fieldName))
)
const financialGroupedFields = computed(() => financialFieldLists.value.groups)
const financialStandaloneFields = computed(() => financialFieldLists.value.ungrouped)

const hasReceiptSection = computed(
  () =>
    hasCombinedRows.value ||
    receiptStandaloneFields.value.length > 0 ||
    receiptGroupedFields.value.length > 0
)

const hasFinancialSection = computed(
  () => financialStandaloneFields.value.length > 0 || financialGroupedFields.value.length > 0
)
const hasVisibleAdditionalFields = computed(
  () =>
    personalAdditionalFields.value.length > 0 ||
    receiptStandaloneFields.value.length > 0 ||
    financialStandaloneFields.value.length > 0 ||
    personalGroupedFields.value.length > 0 ||
    receiptGroupedFields.value.length > 0 ||
    financialGroupedFields.value.length > 0 ||
    hasCombinedRows.value
)

const refreshTables = async () => {
  if (!formData.value?.data) {
    combinedTable.value = createEmptyTable()
    fieldsUsedInTable.value = new Set()
    return
  }
  const data = formDataData.value
  if (!data || Object.keys(data).length === 0) {
    combinedTable.value = createEmptyTable()
    fieldsUsedInTable.value = new Set()
    return
  }

  try {
    const tables = await buildReceiptTables(
      { data },
      {
        includeRepeaterInFinancial: true,
      }
    )
    combinedTable.value = tables?.combinedTable || createEmptyTable()
    fieldsUsedInTable.value = new Set(tables?.fieldsUsedInCombined || [])
  } catch (error) {
    console.warn('Fehler beim Erstellen der Tabellen in StepReview:', error)
    combinedTable.value = createEmptyTable()
    fieldsUsedInTable.value = new Set()
  }
}

watch(
  () => formDataData.value,
  () => {
    refreshTables()
  },
  { deep: true, immediate: true }
)
const getFileIcon = wizard.getFileIcon
const uploadedFiles = wizard.uploadedFiles
const needsSignature = wizard.needsSignature
const hasSignature = wizard.hasSignature
const signatureDataURL = wizard.signatureDataURL

</script>
